import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { clearAllMediaBlobs } from '../../../utils/mediaSessionCache';

const AssignmentContext = createContext(null);

/**
 * Generic provider for the Assignment page subtree.
 *
 * Current responsibilities:
 *  - Tracks `isMediaBusy` (true while a student is recording OR uploading audio/video).
 *  - Allows recorder hooks to register an async "stop-and-wait" handler so that
 *    the assignment end flow can await an in-progress upload before navigating away.
 *  - Hosts mobile Skip / Save actions above the bottom navigation.
 *  - Clears the in-session media blob cache when the assignment unmounts.
 *
 * Adding future state: simply add to the `value` object below.
 */
export const AssignmentProvider = ({ children }) => {
  // True when audio/video is currently recording OR uploading.
  const [isMediaBusy, setIsMediaBusy] = useState(false);

  // DOM host for mobile Skip / Save actions (rendered above BottomNavigation).
  const [mobileActionsHost, setMobileActionsHost] = useState(null);

  // Holds an async fn registered by the active recorder hook.
  // The fn stops the recording (if still running) and returns a promise
  // that resolves only when the upload finishes.
  const stopHandlerRef = useRef(null);

  useEffect(() => {
    return () => {
      clearAllMediaBlobs();
    };
  }, []);

  const registerStopHandler = useCallback((fn) => {
    stopHandlerRef.current = fn;
  }, []);

  const deregisterStopHandler = useCallback(() => {
    stopHandlerRef.current = null;
  }, []);

  /**
   * Awaits the registered stop handler (if any).
   * Called by AssignmentContent before invoking onEndTest so that any
   * in-progress recording/upload can complete first.
   */
  const stopActiveRecording = useCallback(async () => {
    if (stopHandlerRef.current) {
      await stopHandlerRef.current();
    }
  }, []);

  return (
    <AssignmentContext.Provider
      value={{
        isMediaBusy,
        setIsMediaBusy,
        mobileActionsHost,
        setMobileActionsHost,
        registerStopHandler,
        deregisterStopHandler,
        stopActiveRecording
      }}
    >
      {children}
    </AssignmentContext.Provider>
  );
};

export const useAssignmentContext = () => {
  const context = useContext(AssignmentContext);
  if (!context) {
    throw new Error('useAssignmentContext must be used within AssignmentProvider');
  }
  return context;
};
